import { useLocation, useNavigate } from 'react-router';
import { Button } from '../../components';
import Filters from '../../components/Filters/Filters';
import TitleBar from '../../components/TitleBar';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import {
  BCPCard,
  BcpLocationCard,
  BlackswanCard,
  CompanyRevenueCard,
  DependencyMatrixCard,
} from './components';
import { ExposuresChart } from './components/ExposuresChart';
import {
  DivisionsSupported,
  LocationExposure,
  SuppliersMapped,
  TopSuppliersDependencies,
} from './location-risk-hub';
import { useUserContext } from '../../context/PermissionsContext';
import { useEffect, useMemo, useState } from 'react';
import Joyride, { CallBackProps, Placement } from 'react-joyride';
import { useSearchParams } from 'react-router-dom';
import TitleUpdater from '../../components/common/TitleUpdater';
import { useQuery } from '@apollo/client';
import {
  GET_DIVISION_NAME,
  GET_LOCATION_NAME,
} from '../../graphql/utils/queries';

type RiskHubProps = Readonly<{
  type?: string;
}>;

function RiskHub({ type }: RiskHubProps) {
  const navigate = useNavigate();
  let [searchParams] = useSearchParams();
  const divisionUuid = searchParams.get('division');
  const locationUuid = searchParams.get('location');
  const location = useLocation();

  const [run, setRun] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { isCorporate, isLocation, isDivision, selectedTags } =
    useUserContext();

  const IS_LOCATION_RISK_HUB = type === 'location' || isLocation;

  const SHOULD_SHOW_SKELETONS =
    (isDivision && !divisionUuid && !IS_LOCATION_RISK_HUB) ||
    (isLocation && !IS_LOCATION_RISK_HUB) ||
    (!locationUuid && IS_LOCATION_RISK_HUB);

  const { data: divisionData } = useQuery(GET_DIVISION_NAME, {
    variables: { uuid: divisionUuid },
    skip: !divisionUuid,
  });

  const { data: locationData } = useQuery(GET_LOCATION_NAME, {
    variables: { uuid: locationUuid },
    skip: !locationUuid,
  });

  const steps = [
    {
      target: '#filter-dropdown',
      content: `Select a ${
        IS_LOCATION_RISK_HUB ? 'Location' : 'Division'
      } to view its risk profile 📊`,
      placement: 'right' as Placement,
      disableBeacon: true,
      hideFooter: true,
      spotlightPadding: 5,
    },
  ];

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, index } = data;

    if (action === 'close') {
      setRun(false);
      return;
    }

    if (action === 'next' && isDropdownOpen) {
      setStepIndex(index);
    }
  };

  const toggleDropdown = (isOpen: boolean) => {
    setIsDropdownOpen(isOpen);
    if (!isOpen) {
      setStepIndex((prev) => prev + 1);
    }
  };

  const breadcrumbLabel = useMemo(() => {
    if (locationData?.findLocationByUuid?.locationName) {
      return locationData.findLocationByUuid.locationName;
    }
    if (divisionData?.findDivisionByUuid?.name) {
      return divisionData.findDivisionByUuid.name;
    }
  }, [divisionData, locationData]);

  const unifiedLabel =
    breadcrumbLabel ?? (IS_LOCATION_RISK_HUB ? 'Location' : '');

  useEffect(() => {
    if (!hasShown) {
      setHasShown(true);
      setRun(true);
    }
  }, [hasShown]);

  useEffect(() => {
    if (!IS_LOCATION_RISK_HUB) return;

    const isOnRiskHub = location.pathname === '/location-risk-hub';

    if (isLocation && selectedTags?.length === 1) {
      const newPath = `/location-risk-hub?location=${selectedTags[0]}`;
      if (location.pathname + location.search !== newPath) {
        navigate(newPath, { replace: true });
      }
    } else if (!isOnRiskHub) {
      navigate('/location-risk-hub', { replace: true });
    }
  }, [
    isLocation,
    selectedTags,
    location.pathname,
    location.search,
    navigate,
    IS_LOCATION_RISK_HUB,
  ]);

  useEffect(() => {
    if (IS_LOCATION_RISK_HUB) return;
    if (!isDivision || !selectedTags?.length) return;

    const newUrl = `/risk-hub?division=${selectedTags[0]}`;
    const currentUrl = `${location.pathname}${location.search}`;

    if (currentUrl !== newUrl) {
      navigate(newUrl, { replace: true });
    }
  }, [
    isDivision,
    selectedTags,
    location.pathname,
    location.search,
    navigate,
    IS_LOCATION_RISK_HUB,
  ]);

  return (
    <>
      <TitleUpdater title={`${unifiedLabel} Risk Hub`} />
      <TitleBar
        title={`${IS_LOCATION_RISK_HUB ? 'Location' : 'Company'} Risk Hub`}
      />
      <div className="flex flex-col-reverse items-center md:flex-row md:justify-between">
        <div id="filter-dropdown">
          <Filters
            showFilters={[IS_LOCATION_RISK_HUB ? 'location' : 'division']}
            onDropdownToggle={toggleDropdown}
          />
        </div>
        {(isDivision || isCorporate) && (
          <Button
            type="primary"
            className="w-[188px] text-sm"
            onClick={() =>
              navigate(
                `${IS_LOCATION_RISK_HUB ? '/risk-hub' : '/location-risk-hub'}`,
              )
            }
          >
            {IS_LOCATION_RISK_HUB ? 'Corporate Hub' : 'Location Hub'}
          </Button>
        )}
      </div>
      <div
        className={`${
          isDivision ? 'grid gap-4 grid-cols-4' : 'grid--4col gap-4 xl:gap-6'
        }`}
      >
        {SHOULD_SHOW_SKELETONS ? (
          <>
            <Skeleton height={180} className="heartbeat-skeleton" />
            <Skeleton height={180} className="heartbeat-skeleton" />
            <Skeleton height={180} className="heartbeat-skeleton" />
            <Skeleton height={180} className="heartbeat-skeleton" />
          </>
        ) : (
          <>
            <BcpLocationCard />
            <BCPCard />
            <CompanyRevenueCard />
            <BlackswanCard />
            {IS_LOCATION_RISK_HUB && (
              <>
                <LocationExposure />
                <DivisionsSupported />
                <SuppliersMapped />
                <TopSuppliersDependencies />
              </>
            )}
          </>
        )}
      </div>
      {SHOULD_SHOW_SKELETONS ? (
        <div className="w-full grid gap-4 mt-4">
          <Skeleton height={300} className="heartbeat-skeleton" />
          <Skeleton height={300} className="heartbeat-skeleton" />
        </div>
      ) : (
        <>
          <ExposuresChart />
          <DependencyMatrixCard />
        </>
      )}
      {SHOULD_SHOW_SKELETONS && run && (
        <Joyride
          key="joyride"
          steps={steps}
          run={run}
          stepIndex={stepIndex}
          callback={handleJoyrideCallback}
          scrollToFirstStep={false}
          disableCloseOnEsc={true}
          disableOverlayClose={true}
          styles={{
            overlay: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
            spotlight: { backgroundColor: 'rgba(255, 255, 255, 0.9)' },
          }}
        />
      )}
    </>
  );
}

export default RiskHub;
